import { useEffect, useState } from 'react';
import { Copy, Check, AlertTriangle, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

interface PagamentoPixModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** BR Code vindo do servidor. O navegador só desenha, não calcula. */
  payload: string | null;
  valor: string | null;
  erro: string | null;
  confirmando: boolean;
  onConfirmar: () => void;
}

export function PagamentoPixModal({
  isOpen,
  onClose,
  payload,
  valor,
  erro,
  confirmando,
  onConfirmar,
}: PagamentoPixModalProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [falhaQr, setFalhaQr] = useState(false);

  useEffect(() => {
    if (!isOpen || !payload) {
      setQrDataUrl(null);
      setFalhaQr(false);
      return;
    }

    let ativo = true;
    setFalhaQr(false);

    void (async () => {
      try {
        const QRCode = await import('qrcode');
        const url = await QRCode.toDataURL(payload, { margin: 1, width: 320 });
        if (ativo) setQrDataUrl(url);
      } catch {
        // O copia e cola continua servindo mesmo sem a imagem.
        if (ativo) setFalhaQr(true);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [isOpen, payload]);

  useEffect(() => {
    if (!isOpen) setCopiado(false);
  }, [isOpen]);

  const copiar = async () => {
    if (!payload) return;

    try {
      await navigator.clipboard.writeText(payload);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Navegador sem permissão de área de transferência: o texto continua
      // selecionável na tela.
      setCopiado(false);
    }
  };

  const formatarValor = (bruto: string) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      Number.parseFloat(bruto) || 0
    );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Pagamento por PIX">
      <div className="space-y-4">
        {erro ? (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{erro}</span>
          </div>
        ) : (
          <>
            <div className="text-center">
              <p className="text-sm text-gray-500">Valor a receber</p>
              <p className="text-primary-600 text-3xl font-bold">
                {valor ? formatarValor(valor) : '—'}
              </p>
            </div>

            <div className="flex justify-center">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="QR Code para pagamento por PIX"
                  className="h-64 w-64 rounded-lg border border-gray-200"
                />
              ) : (
                <div className="flex h-64 w-64 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 text-gray-400">
                  {falhaQr ? (
                    <>
                      <AlertTriangle className="h-8 w-8" />
                      <span className="px-4 text-center text-xs">
                        Não foi possível desenhar o QR. Use o código copia e cola abaixo.
                      </span>
                    </>
                  ) : (
                    <>
                      <Smartphone className="h-8 w-8 animate-pulse" />
                      <span className="text-xs">Gerando QR...</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="pix-copia-cola" className="text-sm font-medium text-gray-700">
                PIX copia e cola
              </label>
              <div className="flex gap-2">
                <textarea
                  id="pix-copia-cola"
                  readOnly
                  value={payload ?? ''}
                  onFocus={(e) => e.currentTarget.select()}
                  rows={3}
                  className="flex-1 resize-none rounded-lg border border-gray-300 p-2 font-mono text-xs text-gray-700"
                />
                <Button variant="outline" onClick={copiar} disabled={!payload}>
                  {copiado ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                O sistema não verifica o pagamento. Confira o recebimento no aplicativo do banco
                antes de confirmar.
              </span>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={confirmando}>
            Cancelar
          </Button>
          <Button
            onClick={onConfirmar}
            isLoading={confirmando}
            disabled={!payload || Boolean(erro)}
          >
            Confirmar recebimento
          </Button>
        </div>
      </div>
    </Modal>
  );
}
